
import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  HelpCircle,
  Image as ImageIcon,
  MessageSquare,
} from "lucide-react";
import { Grievance } from "@/data/grievances";
import { cn } from "@/lib/utils";
import { addLog } from "@/logger";
import categoryEnvironmental from "@/assets/category-environmental.jpg";
import categoryHealth from "@/assets/category-health.jpg";
import categoryProperty from "@/assets/category-property.jpg";
import categorySocial from "@/assets/category-social.jpg";
import categoryDefault from "@/assets/category-default.jpg";

interface GrievanceCardProps {
  grievance: Grievance;
  index: number;
  /** Resolved URL for the first image (when list fetches image URLs by ID) */
  firstImageUrl?: string | null;
  /** When list API provides update/comment count, show an "N updates" badge. Omit until backend supports it. */
  updateCount?: number;
}

const GrievanceCard: React.FC<GrievanceCardProps> = ({ grievance, index, firstImageUrl: firstImageUrlProp, updateCount }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);


  useEffect(() => {
    const animationDelay = index * 80; // stagger the animations

    if (cardRef.current) {
      cardRef.current.style.opacity = "0";
      cardRef.current.style.transform = "translateY(10px)";

      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.style.transition =
            "opacity 500ms ease, transform 500ms ease";
          cardRef.current.style.opacity = "1";
          cardRef.current.style.transform = "translateY(0)";
        }
      }, animationDelay);
    }
  }, [index]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "Unknown date";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "Invalid date";
    }
  };

  const getStatusIcon = () => {
    const status = grievance.status || "pending";
    switch (status) {
      case "pending":
        return <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />;
      case "in-progress":
        return <Clock className="w-3.5 h-3.5 text-blue-500" />;
      case "resolved":
        return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
      case "done":
        return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
      case "closed":
        return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
      case "open":
        return <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />;
      case "investigating":
        return <Clock className="w-3.5 h-3.5 text-blue-500" />;
      default:
        return <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />;
    }
  };

  const getCategoryColor = () => {
    const category = grievance?.category || "other";
    switch (category) {
      case "environmental":
        return "bg-emerald-50 text-emerald-800 border-emerald-200";
      case "health":
        return "bg-red-50 text-red-800 border-red-200";
      case "property":
        return "bg-indigo-50 text-indigo-800 border-indigo-200";
      case "social":
        return "bg-amber-50 text-amber-800 border-amber-200";
      case "other":
        return "bg-gray-50 text-gray-800 border-gray-200";
      default:
        return "bg-gray-50 text-gray-800 border-gray-200";
    }
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const getCategoryImage = () => {
    const category = grievance?.category || "other";
    switch (category) {
      case "environmental":
        return categoryEnvironmental;
      case "health":
        return categoryHealth;
      case "property":
        return categoryProperty;
      case "social":
        return categorySocial;
      default:
        return categoryDefault;
    }
  };

  // Use resolved URL from parent (list fetches by file ID), or fallback to grievance.images[0] if it's already a URL string
  const firstImageFromGrievance = Array.isArray(grievance?.images) && grievance.images.length > 0 && typeof grievance.images[0] === "string" ? grievance.images[0] : null;
  const firstImageUrl = (firstImageUrlProp != null && firstImageUrlProp !== "" ? firstImageUrlProp : firstImageFromGrievance?.startsWith("http") ? firstImageFromGrievance : null) ?? null;
  const hasValidImage = Boolean(firstImageUrl && !imageError);
  const showImageSection = true; // Always show image section with category defaults

  // Guard against undefined grievance
  if (!grievance || grievance.id == null || grievance.id === "") {
    return null;
  }

  return (
    <div ref={cardRef} className="opacity-0">
      <Link to={`/case/${grievance.id}`} className="block">
        <div className="glass-card hover:shadow-md border rounded-lg overflow-hidden transition-all duration-300 group">
          {showImageSection && (
            <div className="relative h-28 w-full max-w-full overflow-hidden">
              <div className="absolute inset-0 bg-black/20 z-10 group-hover:bg-black/10 transition-colors duration-300" />
              
              {hasValidImage && firstImageUrl ? (
                <img
                  src={firstImageUrl}
                  alt={grievance.title || "Grievance image"}
                  className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                />
              ) : (
                <img
                  src={getCategoryImage()}
                  alt={`${grievance.category || 'default'} category image`}
                  className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                />
              )}
              
              {grievance.category && (
                <div className="absolute bottom-2 left-2 z-20">
                  <span
                    className={cn(
                      "text-xs py-0.5 px-1.5 rounded-full border text-xs",
                      getCategoryColor()
                    )}
                  >
                    {typeof grievance.category === "string" && grievance.category
                      ? grievance.category.charAt(0).toUpperCase() + grievance.category.slice(1)
                      : "Other"}
                  </span>
                </div>
              )}
              {typeof updateCount === "number" && updateCount > 0 && (
                <div className="absolute top-2 right-2 z-20">
                  <span
                    className="flex items-center gap-1 rounded-full bg-primary/90 text-primary-foreground px-2 py-0.5 text-xs font-medium"
                    aria-label={`${updateCount} update${updateCount !== 1 ? "s" : ""}`}
                  >
                    <MessageSquare className="h-3 w-3" aria-hidden />
                    {updateCount}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="p-3">
            <div className="flex justify-between items-start mb-1.5">
              <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors duration-200">
                {grievance.title || "Untitled Grievance"}
              </h3>
              <div className="flex items-center bg-muted px-1.5 py-0.5 rounded text-xs font-medium mt-0.5 ml-1.5 whitespace-nowrap">
                {getStatusIcon()}
                <span className="ml-1 first-letter:uppercase text-xs">
                  {grievance.status || "pending"}
                </span>
              </div>
            </div>

            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
              {grievance.description || "No description available"}
            </p>

            <div className="flex flex-col text-xs text-muted-foreground space-y-0.5">
              <div className="flex items-center">
                <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate text-xs">
                  {grievance.location || "Unknown location"}
                </span>
              </div>
              <div className="flex items-center">
                <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="text-xs">{formatDate(grievance.date)}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default GrievanceCard;
